import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import AnswerContainer from './answer-container/answer-container';
import styles from './unscramble-activity.style';
import { TemplateType } from '../../../../enums/template-type';
import { useColors } from '../../../../hooks';
import { useActivityContext } from '../../../../hooks/use-activity.context';
import useDevice from '../../../../hooks/use-device';
import { useErrorSound } from '../../../../hooks/use-error-sound';
import { useSuccessSound } from '../../../../hooks/use-success-sound';
import useVibration from '../../../../hooks/use-vibration';
import { shuffle } from '../../../../utils/shuffle';
import { AudioPlayerWithProvider } from '../../../audio-player/audio-player-with-provider';
import Fragment, { FragmentOptionType } from '../../../fragment/fragment';
import { HintButton, HintButtonType } from '../../../hint-button/hint-button';
import KeyboardLegend, {
  KeyboardLegendPadding,
} from '../../../keyboard-legend/keyboard-legend';
import LessonImage from '../../../lesson-image/lesson-image';
import LessonPlayerFrame from '../../../lesson-player-frame/lesson-player-frame';
import ThemedText2 from '../../../themed-text-2';
import { ThemedView } from '../../../themed-view';
import TranslatableText2 from '../../../translatable-text2';

export interface UnscrambleActivityInterface {
  type: 'UNSCRAMBLE' | 'UNSCRAMBLE - EXTENDED';
  instructions: string;
  instructionsTranslation?: string;
  text: string;
  image: string;
  audio?: string;
  correctFeedback: string;
  correctFeedbackTranslation?: string;
  incorrectFeedback: string;
  incorrectFeedbackTranslation?: string;
}

export function UnscrambleActivity() {
  const { t } = useTranslation();
  const colors = useColors();
  const { isDesktop } = useDevice();
  const activityContext = useActivityContext();
  const correctSound = useSuccessSound();
  const incorrectSound = useErrorSound();
  const { vibrate } = useVibration();
  const [isError, setError] = useState(false);
  const [isComplete, setComplete] = useState(false);
  const { completeCurrentActivity, isCurrentActivityComplete } =
    activityContext;
  const [userAnswer, setUserAnswer] = useState<FragmentOptionType[]>([]);
  const [options, setOptions] = useState<FragmentOptionType[]>([]);
  const [hintButtonType, setHintButtonType] = useState<HintButtonType>('hint');
  const [showHintButton, setShowHintButton] = useState(false);
  const activity =
    activityContext.currentActivity as UnscrambleActivityInterface;

  const correctParts = useMemo(() => {
    const obtainPartsFromText = (text) => {
      const parts: string[] = [];
      const re = /\[\[(.*?)\]\]/gi;
      let match;
      while ((match = re.exec(text))) {
        parts.push(match[1]);
      }
      return parts;
    };

    if (!activity.text) {
      return [];
    }

    return obtainPartsFromText(activity.text);
  }, [activity.text]);

  const hasAnsweredAll = useMemo(() => {
    return userAnswer.every((answer) => !!answer);
  }, [userAnswer]);

  const shuffledParts = useMemo(() => {
    const shuffledParts = shuffle(correctParts).map((text, key) => ({
      text,
      key,
    }));
    setOptions(shuffledParts);
    setUserAnswer(() => shuffledParts.map(() => null));
    return shuffledParts;
  }, [correctParts]);

  // This is a function to reset only the wrong answers
  // It was defined in the beginning to reset only the wrong ones, but they changed it to reset everything.
  // I'll leave it here if they change their mind again in the future, we just need to uncomment it.

  // function resetWrongUserAnswers() {
  //   setOptions(
  //     userAnswer.map((answer, index) =>
  //       correctParts[index] === answer?.text ? null : answer
  //     )
  //   );
  //   setUserAnswer(
  //     userAnswer.map((answer, index) =>
  //       correctParts[index] === answer?.text ? answer : null
  //     )
  //   );
  // }

  // function showWrongAnswers() {
  //   setUserAnswer(
  //     userAnswer.map((answer, index) =>
  //       answer && correctParts[index] !== answer?.text
  //         ? { ...answer, isWrong: true }
  //         : answer
  //     )
  //   );
  // }

  function resetAllUserAnswers() {
    setOptions(shuffledParts);
    setUserAnswer(() => correctParts.map(() => null));
  }

  function checkAnswer() {
    const isCorrect =
      userAnswer.map((answer) => answer?.text).join(' ') ===
      correctParts.join(' ');

    if (isCorrect) {
      correctSound.play();
      completeCurrentActivity();
      setComplete(true);
    } else {
      setError(true);
      incorrectSound.play();
      vibrate();
      // showWrongAnswers();
      setTimeout(() => {
        setShowHintButton(true);
        setError(false);
        resetAllUserAnswers();
      }, 2000);
    }
  }

  const setUserAnswerOptionIndex = useCallback(
    (option: FragmentOptionType, index: number) => {
      setUserAnswer([
        ...userAnswer.slice(0, index),
        option,
        ...userAnswer.slice(index + 1),
      ]);
    },
    [userAnswer]
  );

  const removeOption = useCallback(
    (option: FragmentOptionType) => {
      const index = userAnswer.findIndex((value) => value?.key === option?.key);

      if (index === -1) {
        return;
      }

      setUserAnswerOptionIndex(null, index);
      setOptions(options.map((opt, i) => (i === option?.key ? option : opt)));
    },
    [userAnswer, options, setUserAnswerOptionIndex]
  );

  const addOption = useCallback(
    (option: FragmentOptionType) => {
      const index = userAnswer.findIndex((value) => value === null);

      if (index === -1) {
        return;
      }

      setUserAnswerOptionIndex(option, index);
      setOptions(options.map((opt) => (opt?.key === option?.key ? null : opt)));
    },
    [userAnswer, options, setUserAnswerOptionIndex]
  );

  const fillAnswers = (numOfItems: number) => {
    const hintAnswers: FragmentOptionType[] = [];
    const remainingOptions: FragmentOptionType[] = [...shuffledParts];

    correctParts.forEach((correctText, index) => {
      if (index < numOfItems) {
        const index = remainingOptions.findIndex(
          (value) => value?.text === correctText
        );

        hintAnswers.push(remainingOptions[index]);
        remainingOptions[index] = null;
      } else {
        hintAnswers.push(null);
      }
    });
    setUserAnswer(hintAnswers);
    setOptions(remainingOptions);
  };

  const handleHintPress = () => {
    if (hintButtonType === 'hint') {
      setHintButtonType('answer');
      fillAnswers(Math.round(correctParts.length / 2));
    } else {
      fillAnswers(correctParts.length);
    }
  };

  return (
    <>
      <LessonPlayerFrame
        showCheckButton={hasAnsweredAll && !isComplete}
        onCheckButtonPress={checkAnswer}
        showContinueButton={isComplete}
      >
        <View style={styles.content}>
          <TranslatableText2
            fontWeightStyle="bold"
            fontSize={18}
            style={styles.instructions}
            translation={activity.instructionsTranslation}
            // eslint-disable-next-line react-native/no-inline-styles
            skeletonStyle={{ width: isDesktop ? '60%' : '80%' }}
          >
            {activity.instructions}
          </TranslatableText2>
          {!!activity.audio && (
            <AudioPlayerWithProvider
              url={activity.audio}
              barActiveColor={colors.primary}
              containerStyle={styles.audioPlayer}
              barContainerColor={colors.offWhite}
              inactiveColor={colors.gray}
              activeColor={colors.gray}
              isTextProgressVisible
              isSlowButtonVisible
            />
          )}
          <LessonImage
            style={styles.image}
            showBanner={false}
            imageUrl={activity?.image}
          />
        </View>
        <ThemedView style={styles.answersContainer}>
          <AnswerContainer isSuccess={isComplete} isError={isError}>
            {isComplete && (
              <ThemedText2
                fontWeightStyle="bold"
                fontSize={24}
                type="onPrimary"
              >
                {correctParts.join(' ')}
              </ThemedText2>
            )}
            {!isComplete &&
              userAnswer.map((answer, index) => (
                <Fragment
                  type="answer-primary"
                  onPress={removeOption}
                  key={`option-${index}`}
                  option={answer}
                  placeholderStyle={styles.placeholderStyle}
                />
              ))}
          </AnswerContainer>
          {showHintButton && !isComplete && (
            <HintButton
              onPress={handleHintPress}
              type={hintButtonType}
              containerStyle={styles.hintButton}
            />
          )}
        </ThemedView>
        <View style={styles.optionsContainer}>
          <View style={styles.optionsInnerContainer}>
            {options.map((option, index) => (
              <Fragment
                type="option"
                onPress={addOption}
                key={`option-${index}`}
                option={option}
                placeholderStyle={styles.placeholderStyle}
              />
            ))}
          </View>
        </View>
        {!isComplete && <KeyboardLegendPadding />}
      </LessonPlayerFrame>
      <KeyboardLegend
        templateType={TemplateType.UNSCRAMBLE}
        forceHide={!!userAnswer?.[0] || isCurrentActivityComplete}
        text={t('keyboardLegend.answer')}
        indicatorList={options.map((_, i) => ((i + 1) % 10).toString())}
      />
    </>
  );
}
